import {
  Artifact,
  ArtifactLinksResponse,
  ArtifactType,
  Link,
  LinkType,
  Response,
  SnaphotReference,
} from '../../models'
import axios from 'axios'
import { requireDefined } from '@/utils/helpers'
import { IArtifactService } from '../IArtifactService'

export default class RestArtifactService implements IArtifactService {
  constructor(private backendUrl: string) {}
  public async getLinkedArtifacts(
    projectId: number,
    snapshotId: SnaphotReference,
    id: number,
    direction: 'incoming' | 'outgoing' | 'any',
    filter?: number,
  ): Promise<{ artifacts: Artifact[]; links: Link[] }> {
    const params = {
      filter,
      direction,
    }

    if (snapshotId != 'latest' && (isNaN(snapshotId) || isNaN(id) || isNaN(projectId))) {
      return {
        artifacts: [],
        links: [],
      }
    }

    return axios
      .get<ArtifactLinksResponse>(
        `${this.backendUrl}/project/${projectId}/snapshot/${snapshotId}/artifacts/${id}/links`,
        { params },
      )
      .then(async (response) => {
        // Handle missing or malformed response data
        const included = response.data?.included ?? {
          artifacts: [],
          artifactTypes: [],
          linkTypes: [],
        }
        const data = response.data?.data ?? []

        const artifacts = (included.artifacts ?? []).map((artifact) => ({
          ...artifact,
          artifactType: this.resolveArtifactType(
            included.artifactTypes ?? [],
            artifact.artifactType,
          ),
        }))

        const links = data.map((link) => ({
          ...link,
          linkType: this.resolveLinkType(included.linkTypes ?? [], link.linkType),
        }))

        if (artifacts.length == 0) {
          artifacts.push(await this.getArtifact(projectId, snapshotId, id, filter))
        }

        return {
          artifacts,
          links,
        }
      })
  }

  public async getLinksForArtifacts(
    projectId: number,
    snapshotId: SnaphotReference,
    artifactIds: number[],
    filter?: number,
  ): Promise<{ artifacts: Artifact[]; links: Link[] }> {
    const params = { filter }

    if (snapshotId != 'latest' && (isNaN(snapshotId) || isNaN(projectId))) {
      return { artifacts: [], links: [] }
    }

    return axios
      .post<ArtifactLinksResponse>(
        `${this.backendUrl}/project/${projectId}/snapshot/${snapshotId}/links/artifacts`,
        artifactIds,
        { params },
      )
      .then((response) => {
        const included = response.data?.included ?? {
          artifacts: [],
          artifactTypes: [],
          linkTypes: [],
        }
        const data = response.data?.data ?? []

        const artifacts = (included.artifacts ?? []).map((artifact) => ({
          ...artifact,
          artifactType: this.resolveArtifactType(
            included.artifactTypes ?? [],
            artifact.artifactType,
          ),
        }))

        const links = data.map((link) => ({
          ...link,
          linkType: this.resolveLinkType(included.linkTypes ?? [], link.linkType),
        }))

        return { artifacts, links }
      })
  }

  private resolveArtifactType(artifactTypes: ArtifactType[], artifactTypeId: number): ArtifactType {
    return requireDefined(artifactTypes.find((at) => at.id === artifactTypeId))
  }

  private resolveLinkType(linkTypes: LinkType[], linkTypeId: number): LinkType {
    return requireDefined(linkTypes.find((lt) => lt.id === linkTypeId))
  }

  public async getArtifact(
    projectId: number,
    snapshotId: SnaphotReference,
    id: number,
    filter?: number,
  ): Promise<Artifact> {
    const params = {
      filter,
    }
    return axios
      .get<
        Response<Artifact>
      >(`${this.backendUrl}/project/${projectId}/snapshot/${snapshotId}/artifacts/${id}`, { params })
      .then((response) => response.data.data)
  }

  public async getArtifacts(
    projectId: number,
    snapshotId: SnaphotReference,
    filter?: number,
  ): Promise<Artifact[]> {
    const params = {
      filter,
    }

    return axios
      .get<
        Response<Artifact[]>
      >(`${this.backendUrl}/project/${projectId}/snapshot/${snapshotId}/artifacts_collection`, { params })
      .then((response) => response.data.data)
  }

  public async getArtifactTypes(
    projectId: number,
    snapshotId: SnaphotReference,
    filter?: number,
  ): Promise<ArtifactType[]> {
    const params = {
      filter,
      include: 'artifactCount,unlinkedArtifactCount',
    }

    return axios
      .get<
        Response<ArtifactType[]>
      >(`${this.backendUrl}/project/${projectId}/snapshot/${snapshotId}/artifact_types`, { params })
      .then((response) => response.data.data)
  }

  public async getLinkTypes(
    projectId: number,
    snapshotId: SnaphotReference,
    filter?: number,
  ): Promise<LinkType[]> {
    const params = {
      filter,
    }

    return axios
      .get<
        Response<LinkType[]>
      >(`${this.backendUrl}/project/${projectId}/snapshot/${snapshotId}/link_types`, { params })
      .then((response) => response.data.data)
  }

  public async getArtifactsCollection(
    projectId: number,
    snapshotId: SnaphotReference,
    typeId?: number,
    filter?: number,
    include?: string,
    sortBy?: string,
    page?: number,
    pageSize?: number,
  ): Promise<Artifact[]> {
    const params = {
      typeId,
      filter,
      include,
      sortBy,
      page,
      pageSize,
    }

    return axios
      .get<
        Response<Artifact[]>
      >(`${this.backendUrl}/project/${projectId}/snapshot/${snapshotId}/artifacts_collection`, { params })
      .then((response) => response.data.data)
  }
}
